import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import PageShell from "@/components/PageShell";
import WaitlistForm from "@/components/WaitlistForm";
import MissionStories from "@/components/MissionStories";
import PillarsSection from "@/components/PillarsSection";
import RealityStories from "@/components/RealityStories";
import {
  SafeToSpendSection,
  SmartCartSection,
  SubscriptionsSection,
  VoucherVaultSection,
  TravelBudgetingSection,
  LiveFeedSection,
} from "@/components/FeatureSections";

const Landing = () => {
  return (
    <PageShell navyTheme={true}>
      {/* =============================================================
          HERO — borderless, wordmark flush on the navy canvas
         ============================================================= */}
      <section
        id="hero"
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1000px 500px at 50% -100px, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.00) 60%)",
        }}
        data-testid="hero-section"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 300px at 80% 20%, rgba(183,165,255,0.08), transparent 60%), radial-gradient(500px 300px at 15% 80%, rgba(34,197,133,0.08), transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px] px-4 md:px-6 pt-16 pb-14 md:pt-24 md:pb-20">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
            {/* Original SpendNova stacked logo — transparent PNG, flush on canvas (no card, no border, no background) */}
            <img
              src="/spendnova-logo-transparent.png"
              alt="SpendNova™"
              draggable="false"
              className="select-none w-[240px] sm:w-[280px] md:w-[320px] h-auto"
              data-testid="hero-brand"
            />

            {/* Coming Soon badge — centered directly below SpendNova */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] mt-5"
              style={{
                background: "rgba(34,197,133,0.12)",
                border: "1px solid rgba(34,197,133,0.45)",
                color: "#22C585",
              }}
              data-testid="hero-beta-badge"
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: "#22C585",
                  boxShadow: "0 0 12px rgba(34,197,133,0.9)",
                }}
              />
              Coming Soon — v1.0.0
            </div>

            {/* Tagline — no ™, no trailing period */}
            <h1
              className="mt-5 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
              data-testid="hero-tagline"
            >
              Know What&apos;s Safe to Spend
            </h1>

            <p
              className="mt-4 text-lg md:text-xl text-[#22C585] font-semibold"
              data-testid="hero-subheadline"
            >
              Inspired by military life. Built for every financial journey.
            </p>

            <p
              className="mt-5 text-[15px] md:text-base text-[#C7D0DF] leading-relaxed max-w-2xl mx-auto"
              data-testid="hero-description"
            >
              SpendNova™ helps you know exactly what's safe to spend with
              confidence. Track bills, subscriptions, receipts, TDY per-diem,
              PCS travel expenses, and everyday finances — all in one secure
              place.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center w-full">
              <Link
                to="/install"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-[15px] font-semibold text-white transition-transform hover:-translate-y-[1px] w-full sm:w-auto"
                style={{
                  background: "#22C585",
                  boxShadow:
                    "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                }}
                data-testid="hero-install-cta"
              >
                <Download size={18} />
                Install SpendNova
              </Link>
              <a
                href="#waitlist"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-[15px] font-semibold text-white transition-transform hover:-translate-y-[1px] w-full sm:w-auto"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
                data-testid="hero-waitlist-cta"
              >
                Join the Waitlist
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Safe to Spend */}
      <SafeToSpendSection />

      {/* 3. Smart Cart */}
      <SmartCartSection />

      {/* 4. Subscriptions Tracker */}
      <SubscriptionsSection />

      {/* 5. Voucher Vault — specimen worksheet */}
      <VoucherVaultSection />

      {/* 5.5 Travel Budgeting & Trip Planning */}
      <TravelBudgetingSection />

      {/* 6. Nova Live Feed */}
      <LiveFeedSection />

      {/* Three Primary Pillars — Bunker / Log / Balance */}
      <PillarsSection />

      {/* Mission Stories — kept intact below the feature sections */}
      <MissionStories />

      {/* Reality Stories — Trucker + Freelancer flows */}
      <RealityStories />

      {/* THE PROMISE */}
      <section
        id="the-promise"
        className="relative py-12 md:py-16"
        data-testid="promise-section"
      >
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div
            className="relative rounded-3xl p-7 md:p-10 overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(34,197,133,0.12) 0%, rgba(255,255,255,0.03) 100%)",
              border: "1px solid rgba(34,197,133,0.30)",
              boxShadow:
                "0 40px 80px -40px rgba(34,197,133,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl"
              style={{ background: "#22C585" }}
            />
            <div className="relative max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
                The Promise
              </div>
              <h2
                className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
                style={{ fontFamily: "Sora, Inter, sans-serif" }}
                data-testid="promise-title"
              >
                Your money stays yours.<br />Full stop.
              </h2>
              <p
                className="mt-5 text-[15px] md:text-base text-[#E5EAF3] leading-relaxed"
                data-testid="promise-body-1"
              >
                We do not sell your data. We do not train models on your
                finances. We do not share anything with advertisers. Ever.
              </p>
              <p
                className="mt-3 text-[14px] md:text-[15px] text-[#C7D0DF] leading-relaxed"
                data-testid="promise-body-2"
              >
                Financial data is encrypted in transit (TLS 1.3), encrypted at
                rest, and stored on servers hosted in the United States. Delete
                your account any time — we honor CCPA / GDPR data export
                requests within 5 business days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA + WAITLIST */}
      <section
        id="waitlist"
        className="relative py-14 md:py-20"
        data-testid="cta-section"
      >
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-3xl md:text-5xl font-extrabold text-white leading-tight tracking-tight"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
              data-testid="final-cta-title"
            >
              Ready to know what's safe to spend?
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link
                to="/install"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-base font-semibold text-white transition-transform hover:-translate-y-[1px] w-full sm:w-auto"
                style={{
                  background: "#22C585",
                  boxShadow:
                    "0 12px 34px -12px rgba(34,197,133,0.65), inset 0 1px 0 rgba(255,255,255,0.20)",
                }}
                data-testid="final-cta-install"
              >
                <Download size={18} />
                Get SpendNova now
              </Link>
            </div>
            <p
              className="mt-4 text-sm text-[#A8B0BE]"
              data-testid="final-cta-caption"
            >
              iOS · iPadOS · Android · one link, auto-detects your device
            </p>
          </div>
          <div className="mt-12 max-w-xl mx-auto">
            <div
              className="rounded-3xl p-6 md:p-8"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
              data-testid="waitlist-card"
            >
              <div className="text-center">
                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
                  Waitlist
                </div>
                <h3
                  className="mt-2 text-xl md:text-2xl font-extrabold text-white"
                  style={{ fontFamily: "Sora, Inter, sans-serif" }}
                  data-testid="waitlist-title"
                >
                  Get early access to SpendNova<span className="text-[#22C585]">™</span>.
                </h3>
                <p className="mt-2 text-sm text-[#C7D0DF]">
                  Prefer to hear before you install? Drop your email and we'll
                  ping you the moment we ship.
                </p>
              </div>
              <div className="mt-6">
                <WaitlistForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Landing;
